import type { ProfileHalf, ReadingMetric } from '../api/profileReading';

/**
 * Every sentence about the span comes from the dates that were read.
 *
 * The design asked for "nos últimos 90 dias". The public endpoint answers with
 * the twelve most recent posts and a cursor, which for someone who posts weekly
 * is a season and for someone who posts daily is a fortnight. Printing a fixed
 * window would be true for one of them and false for the other, so this reads
 * the window off the data instead.
 */
export function windowSentence(reading: ProfileHalf): string {
  const { postsRead, oldestAt, partial } = reading.window;
  const since = monthAndDay(oldestAt);

  // One post is not a window, so it gets a sentence of its own rather than
  // "entre as suas 1 publicação mais recentes".
  if (postsRead === 1) {
    return `Na sua publicação mais recente, de ${since}.`;
  }

  return partial
    ? `Entre as suas ${postsRead} publicações mais recentes, desde ${since}.`
    : `Entre as suas ${postsRead} publicações, desde ${since}.`;
}

const MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro'
];

/**
 * "12 de março", and "7 de maio de 2025" when it is not this year.
 *
 * The year is not decoration. The reading window on a profile that posts rarely
 * reaches back further than anyone expects — measured on a real account, twelve
 * posts covered fifteen months — and "desde 7 de maio" reads as this year to
 * everyone. A date that quietly means last year is the reading lying about how
 * old its own evidence is.
 */
export function monthAndDay(iso: string, today = new Date()): string {
  const date = new Date(iso);
  const day = `${date.getUTCDate()} de ${MONTHS[date.getUTCMonth()]}`;
  return date.getUTCFullYear() === today.getUTCFullYear()
    ? day
    : `${day} de ${date.getUTCFullYear()}`;
}

const FORMAT_NAMES: Record<ProfileHalf['topPost']['format'], string> = {
  reel: 'Reel',
  video: 'Vídeo',
  carousel: 'Carrossel',
  image: 'Foto'
};

export function formatName(format: ProfileHalf['topPost']['format']): string {
  return FORMAT_NAMES[format];
}

/**
 * "1,7 mil", and "8,7 mi" past a million — the same rounding Instagram
 * prints beside the post she is looking at.
 *
 * Below a thousand the exact number is printed: 320 curtidas is a fact she can
 * check against her own screen, and "0,3 mil" would read as an approximation of
 * something she can count.
 */
export function compactCount(value: number): string {
  if (value < 1000) {
    return String(value);
  }
  const thousands = value / 1000;
  // The unit follows the rounded figure, not the raw one: 999.950 would
  // otherwise print as "1000,0 mil".
  if (thousands < 999.95) {
    return `${thousands.toFixed(1).replace('.', ',')} mil`;
  }
  return `${(value / 1_000_000).toFixed(1).replace('.', ',')} mi`;
}

const METRIC_LABELS: Record<ReadingMetric['kind'], string> = {
  likes: 'curtidas',
  comments: 'comentários',
  views: 'visualizações',
  engagement: 'de engajamento'
};

export function metricLabel(kind: ReadingMetric['kind']): string {
  return METRIC_LABELS[kind];
}

/**
 * What to say about a number that is not there.
 *
 * Returns undefined when there is nothing worth saying: a photo having no view
 * count is not news to the person who posted the photo. A count she switched
 * off is, because it is the reason her best post might not be the one shown.
 */
export function missingMetricNote(metrics: readonly ReadingMetric[]): string | undefined {
  const withheld = metrics.filter((metric) => metric.state === 'withheld');
  if (withheld.length === 0) {
    return undefined;
  }
  const names = withheld.map((metric) => metricLabel(metric.kind)).join(' e ');
  return `Esse perfil esconde ${names}, então a conta foi feita sem elas.`;
}
